import { useTableFilter } from '@welbe/web-design-system';
import Analytics from 'helpers/analytics';
import { useWindowSize } from 'hooks/useWindowSize';
import { AxiosResponse } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { loggerInfo } from 'utils/infoLog';
import { getCampaign } from '../Evaluations/components/Header/Header.services';
import { getColorMarker } from '../Evaluations/components/Header/helpers/getColorMarket';
import {
	getCollaborators,
	getCommunications,
	postReferEmployee,
} from './services/collaborators';
import { useToast } from '@welbe/web-design-system';

export interface ICommunications {
	id: string;
	name: string;
	technicalName: string;
	sequence: number | undefined;
}

interface IEmployeeDetails {
	visible: boolean;
	employeeId: string;
	campaignAnswerId: string;
}

const communicationCreatedTypes = [
	'introduction',
	'sensitization',
	'evaluation',
	'closing',
];

const reminderOption = {
	id: 'nom035-reminder',
	value: 'Recordatorio',
};

const emptyCollaborators = {
	data: { content: [], meta: { totalPages: 0, totalAmount: 0 } },
} as unknown as AxiosResponse;

export const useCollaborators = () => {
	const { toast } = useToast();
	const [isCommunicationOpen, setCommunication] = useState<boolean>(false);
	const campaignId: string = window.location.pathname.split('/').pop() || '';
	const params = new URLSearchParams(window.location.search);
	const communicationId = params.get('communicationId');
	const [isReferVisible, setReferVisible] = useState<boolean>(false);
	const [isWithoutOccupationalDoctor, setWithoutOccupationalDoctor] =
		useState<boolean>(false);
	const [employeeDetails, setEmployeeDetails] = useState<IEmployeeDetails>({
		visible: false,
		employeeId: '',
		campaignAnswerId: '',
	});
	const [referProcessingStatus, setReferProcessingStatus] = useState<{
		[key: string]: number;
	}>({
		success: 0,
		alreadyReferred: 0,
		error: 0,
	});
	const [isLoading, setLoading] = useState<boolean>(false);
	const [isReferredModalVisible, setReferredModalVisible] =
		useState<boolean>(false);
	const [amount, setAmount] = useState<number>(0);
	const [selectedEmployees, setSelectedEmployees] = useState<[]>([]);
	const [communications, setCommunications] = useState<ICommunications[]>([]);
	const [isLoadingCommunication, setLoadingCommunication] = useState(false);
	const [bookDates, setBookDates] = useState<Date[]>([]);
	const { height } = useWindowSize();
	const [isFilterOpen, setFilterOpen] = useState<boolean>(true);

	const pickerSelected = bookDates?.map((bookDate: any, index: any) => ({
		color: getColorMarker(index),
		date: bookDate,
	}));

	const [selectedCommunication, setSelectedCommunication] =
		useState<ICommunications>({
			id: '',
			name: '',
			technicalName: '',
			sequence: undefined,
		});

	const latestRequest = useRef<Promise<AxiosResponse>>();

	// Só busca com questionnaireId definido; sem ele devolve o último resultado
	// (ou vazio) sem chamar a API. Respostas antigas devolvem a da busca mais
	// recente, para não sobrescrever a lista.
	const searchCollaborators = (payload: any): Promise<AxiosResponse> => {
		const { questionnaireId } = payload;
		if (!questionnaireId || questionnaireId === 'undefined') {
			return latestRequest.current ?? Promise.resolve(emptyCollaborators);
		}

		const request = getCollaborators(payload);
		latestRequest.current = request;

		return request.then((response) =>
			latestRequest.current === request ? response : latestRequest.current!,
		);
	};

	const {
		data,
		filters,
		loading,
		totalPages,
		currentPage,
		handleSearch,
		handleFilters,
		handleChangePage,
	} = useTableFilter({
		service: searchCollaborators,
		instantSearch: ['branchOfficeId', 'result', 'status', 'questionnaireId'],
		preventFirstRender: true,
		tableFilters: {
			campaignId: campaignId,
			questionnaireId: '',
			employee: '',
			pageSize: 50,
			pageNumber: 0,
			result: '',
			status: '',
		},
	});

	const fetchDates = async () => {
		setLoadingCommunication(true);
		try {
			const {
				data: { content: dataCampaign },
			} = await getCampaign(campaignId);

			setBookDates(
				dataCampaign.communications.map(
					(comunication: any) => comunication.sendingDateTime,
				),
			);
		} catch (error) {
			toast.error(
				'Hubo un error al intentar procesar esta operación. Inténtalo de nuevo más tarde.',
			);
		} finally {
			setLoadingCommunication(false);
		}
	};

	const handleSelectCommunication = (index: number) => {
		setSelectedCommunication(communications[index]);
		setLoadingCommunication(true);
		setTimeout(() => {
			setLoadingCommunication(false);
		}, 500);
	};

	const handleCloseModalWithoutOccupationalDoctor = () => {
		setWithoutOccupationalDoctor(false);
	};

	const handleRefer = (type: string, selected: any) => {
		if (type === 'all') {
			setSelectedEmployees([]);
			setAmount(selected);
		}
		if (type === 'table') {
			const selectedMapped = selected.map((employee: any) => {
				return employee.id;
			});
			setSelectedEmployees(selectedMapped);
			setAmount(selected.length);
		}
		setReferVisible(true);
	};

	const handlePostRefer = async () => {
		const attributeName = selectedEmployees.length
			? 'employeeIds'
			: 'allEmployees';

		const attributeValue =
			attributeName === 'employeeIds' ? selectedEmployees : true;

		const payload = {
			[attributeName]: attributeValue,
		};

		try {
			setLoading(true);
			const { data } = await postReferEmployee(
				campaignId,
				selectedCommunication.id,
				payload,
			);
			Analytics.track('campaign', 'refer-employees', {
				selectedEmployees: selectedEmployees,
			});
			setReferProcessingStatus(data.content);
			setReferVisible(false);
			setReferredModalVisible(true);
		} catch (error) {
			const { response } = error;

			if (
				response.status === 400 &&
				response.data.errors.some((e: any) => e.message === 'Doctors not found')
			) {
				setReferVisible(false);
				setWithoutOccupationalDoctor(true);
				return;
			}
			loggerInfo({
				data: error,
				context: 'campaign',
				location: 'handlePostRefer',
			});
			toast.error(
				'Hubo un error al intentar procesar esta operación. Inténtalo de nuevo más tarde.',
			);
		} finally {
			setLoading(false);
		}
	};

	const handleCloseReferModal = () => {
		setSelectedEmployees([]);
		setReferVisible(false);
	};

	const handleOpenEmployeeDetail = (employee: any) => {
		setEmployeeDetails({
			visible: true,
			employeeId: employee.id,
			campaignAnswerId: employee.campaignAnswerId,
		});
	};

	const handleChangePageSize = (pageSize: number) => {
		handleFilters({ pageSize });
	};

	const handleSelectEmployees = (employees: any) => {
		setSelectedEmployees(employees);
	};

	const fetchCommunications = async () => {
		setLoadingCommunication(true);
		try {
			const { data } = await getCommunications(campaignId);
			setCommunications([...data.content]);
		} catch (error) {
			loggerInfo({
				data: error,
				context: 'campaign',
				location: 'fetchCommunications',
			});
		} finally {
			setLoadingCommunication(false);
		}
	};

	useEffect(() => {
		if (selectedCommunication.id) {
			handleFilters({
				questionnaireId: selectedCommunication.id,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCommunication]);

	useEffect(() => {
		fetchCommunications();
		fetchDates();

		return () =>
			setReferProcessingStatus({
				success: 0,
				alreadyReferred: 0,
				error: 0,
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const [firstCommunication] = communications;
		const startCommunication =
			communications.find(
				(communication) => communication.id === communicationId,
			) || firstCommunication;
		setSelectedCommunication({ ...startCommunication });
	}, [communications, communicationId]);

	return {
		amount,
		isReferVisible,
		isLoadingCommunication,
		selectedCommunication,
		communications,
		campaignId,
		isReferredModalVisible,
		isLoading,
		referProcessingStatus,
		employeeDetails,
		isCommunicationOpen,
		communicationCreatedTypes,
		reminderOption,
		pickerSelected,
		isWithoutOccupationalDoctor,
		setCommunication,
		setEmployeeDetails,
		handleCloseReferModal,
		setReferredModalVisible,
		setReferVisible,
		handleRefer,
		handlePostRefer,
		handleSelectCommunication,
		handleCloseModalWithoutOccupationalDoctor,
		selectedEmployees,
		setSelectedEmployees,
		height,
		isFilterOpen,
		setFilterOpen,
		data,
		loading,
		totalPages,
		currentPage,
		handleSearch,
		handleFilters,
		handleChangePage,
		handleChangePageSize,
		handleSelectEmployees,
		handleOpenEmployeeDetail,
		filters,
	};
};
